import {token} from "@atlaskit/tokens";
import type {Dispatch, RefObject, SetStateAction} from "react";
import {useCallback, useMemo, useState} from "react";
import {
    GenericDispositionSelector,
    type DispositionSelectionState,
    type GenericDispositionSelectorHandle,
    type GenericDispositionSelectorOutcome,
} from "~/components/dispositionSelector";
import {EventContainerDetailsTriggerEvent} from "~/components/eventContainerDetails/eventContainerDetailsTriggerEvent";
import type {OttopilotDispositionConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerDispositionConfig";
import {
    OttopilotTrigger,
    type OttopilotTriggerConfig,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTriggerConfig";
import {isTriggerEventFormVisible, resolveActiveTrigger} from "~/utilities/eventContainerTriggerEvent";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeTooltip} from "@vesta/ananke/components/anankeTooltip";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import {getEventContainerDispositionId} from "~/utilities/ottopilotSemanticColumns";

export function getDispositionSaveDisabledReason({
    eventContainerDispositionId,
    selectionState,
    tempDispositionId,
    triggerConfig,
    triggerEvent,
}: {
    eventContainerDispositionId: Uuid | null | undefined;
    selectionState: DispositionSelectionState;
    tempDispositionId: Uuid | null;
    triggerConfig: OttopilotTriggerConfig | undefined;
    triggerEvent: {additionalColumnValues: Record<Uuid, unknown>} | null;
}): string | null {
    if (selectionState === "inProgress") {
        return "Complete the disposition selection before saving";
    }
    if (tempDispositionId === null) {
        return "Select a disposition before saving";
    }
    if (tempDispositionId === eventContainerDispositionId) {
        return "Change the disposition before saving";
    }
    if (triggerConfig !== undefined && isTriggerEventFormVisible(triggerConfig, tempDispositionId) === true) {
        if (triggerEvent === null) {
            return "Schedule a call before saving";
        }
        const activeTrigger = resolveActiveTrigger(triggerConfig, tempDispositionId);
        if (activeTrigger !== undefined && activeTrigger.type === OttopilotTrigger.dateTimeTrigger) {
            const scheduleValue = triggerEvent.additionalColumnValues[activeTrigger.dateTimeColumnId];
            if (scheduleValue === null || scheduleValue === undefined) {
                const scheduleLabel = activeTrigger.scheduleFieldLabel ?? "Schedule";
                return `Please enter ${scheduleLabel} before saving`;
            }
        }
    }
    return null;
}

export function DispositionPanelFooterActions({
    saveLabel,
    saveDisabledReason,
    isSavePending,
    onCancel,
    onSave,
}: {
    saveLabel: string;
    saveDisabledReason: string | null;
    isSavePending: boolean;
    onCancel: () => void;
    onSave: () => void;
}) {
    const isSaveDisabled = saveDisabledReason !== null;

    return (
        <>
            <AnankeButton
                type="button"
                appearance="subtle"
                onClick={onCancel}
            >
                Cancel
            </AnankeButton>
            <AnankeTooltip
                content={saveDisabledReason ?? ""}
                position="bottom"
            >
                {(tooltipProps) => (
                    <AnankeButton
                        {...tooltipProps}
                        type="button"
                        isDisabled={isSaveDisabled}
                        appearance="primary"
                        isLoading={isSavePending}
                        onClick={onSave}
                    >
                        {saveLabel}
                    </AnankeButton>
                )}
            </AnankeTooltip>
        </>
    );
}

export function EventContainerDetailsDispositionPanel({
    dispositionSelectorRef,
    eventContainer,
    eventContainerTemplate,
    workspaceId,
    dispositions,
    dispositionConfig,
    triggerConfig,
    tempDispositionId,
    setTempDispositionId,
    setTempSuggestedStatus,
    triggerEvent,
    setTriggerEvent,
    remarksColumn,
    tempRemarks,
    setTempRemarks,
    eventContainerWithDiff,
    onDispositionCancel,
    onDispositionSave,
    isDispositionSavePending,
    isDispositionUpdateDisabled,
    dispositionUiLevels,
    hideActions = false,
    onSelectionStateChange,
}: {
    dispositionSelectorRef: RefObject<GenericDispositionSelectorHandle | null>;
    eventContainer: EventContainerWithEvents;
    eventContainerTemplate: EventContainerTemplate;
    workspaceId: Uuid;
    dispositions: Array<{id: Uuid; displayName: string}>;
    dispositionConfig: OttopilotDispositionConfig | undefined;
    triggerConfig: OttopilotTriggerConfig | undefined;
    tempDispositionId: Uuid | null;
    setTempDispositionId: Dispatch<SetStateAction<Uuid | null>>;
    setTempSuggestedStatus: Dispatch<SetStateAction<Uuid | null>>;
    triggerEvent: {additionalColumnValues: Record<Uuid, unknown>} | null;
    setTriggerEvent: Dispatch<SetStateAction<{additionalColumnValues: Record<Uuid, unknown>} | null>>;
    remarksColumn: Uuid | null;
    tempRemarks: string | null;
    setTempRemarks: Dispatch<SetStateAction<string | null>>;
    eventContainerWithDiff: EventContainerWithEvents;
    onDispositionCancel: () => void;
    onDispositionSave: () => void;
    isDispositionSavePending: boolean;
    isDispositionUpdateDisabled: boolean;
    dispositionUiLevels?: number;
    /** When true, Cancel/Update are not rendered here — use aside panel `footer` instead. */
    hideActions?: boolean;
    onSelectionStateChange?: (state: DispositionSelectionState) => void;
}) {
    const [selectionState, setSelectionState] = useState<DispositionSelectionState>("completed");
    const eventContainerDispositionId = getEventContainerDispositionId(eventContainer, eventContainerTemplate);

    const currentDispositionDisplayName = dispositions.find((disposition) => disposition.id === eventContainerDispositionId)?.displayName ?? "-";

    const handleOutcomeChange = useCallback(
        (outcome: GenericDispositionSelectorOutcome) => {
            if (outcome.dispositionId === null) {
                return;
            }
            setTempDispositionId((currentDispositionId) => (currentDispositionId === outcome.dispositionId ? currentDispositionId : outcome.dispositionId));
            setTempSuggestedStatus((currentSuggestedStatus) =>
                currentSuggestedStatus === outcome.suggestedJourneyStatus ? currentSuggestedStatus : outcome.suggestedJourneyStatus,
            );
        },
        [setTempDispositionId, setTempSuggestedStatus],
    );

    const handleSelectionStateChange = useCallback(
        (state: DispositionSelectionState) => {
            setSelectionState(state);
            onSelectionStateChange?.(state);
        },
        [onSelectionStateChange],
    );

    const dispositionSaveDisabledReason = useMemo(
        () =>
            getDispositionSaveDisabledReason({
                eventContainerDispositionId: eventContainerDispositionId,
                selectionState: selectionState,
                tempDispositionId: tempDispositionId,
                triggerConfig: triggerConfig,
                triggerEvent: triggerEvent,
            }),
        [selectionState, eventContainerDispositionId, tempDispositionId, triggerConfig, triggerEvent],
    );

    if (isDispositionUpdateDisabled === true) {
        return (
            <>
                <div className="grid grid-flow-col grid-cols-[7.75rem_auto] w-full gap-4">
                    <div
                        className=" font-ds-font-weight-regular leading-ds-space-250 text-[0.875rem] p-ds-space-100"
                        style={{
                            color: token("color.text"),
                            font: "Inter",
                        }}
                    >
                        <div className="w-full">Disposition</div>
                    </div>
                    <div
                        className="p-ds-space-100 text-[0.875rem] leading-ds-space-250"
                        style={{
                            color: token("color.text"),
                        }}
                    >
                        {currentDispositionDisplayName}
                    </div>
                </div>
                {remarksColumn !== null && (
                    <div className="grid grid-flow-col grid-cols-[7.75rem_auto] w-full gap-4">
                        <div
                            className=" font-ds-font-weight-regular leading-ds-space-250 text-[0.875rem] p-ds-space-100"
                            style={{
                                color: token("color.text"),
                                font: "Inter",
                            }}
                        >
                            <div className="w-full">Remarks</div>
                        </div>
                        <div
                            className="p-ds-space-100 text-[0.875rem] leading-ds-space-250"
                            style={{
                                color: token("color.text"),
                            }}
                        >
                            {(eventContainerWithDiff.additionalColumnValues[remarksColumn] as string | null | undefined) ?? "-"}
                        </div>
                    </div>
                )}
            </>
        );
    }

    return (
        <>
            <GenericDispositionSelector
                ref={dispositionSelectorRef}
                journey={eventContainer}
                workspaceId={workspaceId}
                dispositions={dispositions}
                value={tempDispositionId}
                dispositionConfig={dispositionConfig}
                alwaysVisibleLevels={dispositionUiLevels}
                onOutcomeChange={handleOutcomeChange}
                onSelectionStateChange={handleSelectionStateChange}
            />
            <EventContainerDetailsTriggerEvent
                setTriggerEvent={setTriggerEvent}
                triggerEvent={triggerEvent}
                selectedDispositionId={tempDispositionId}
                triggerConfig={triggerConfig}
            />
            {remarksColumn !== null && (
                <div className="grid grid-flow-col grid-cols-[7.75rem_auto] w-full gap-4">
                    <div
                        className=" font-ds-font-weight-regular leading-ds-space-250 text-[0.875rem] p-ds-space-100"
                        style={{
                            color: token("color.text"),
                            font: "Inter",
                        }}
                    >
                        <div className="w-full">Call Remarks</div>
                    </div>
                    <div className="w-full h-full">
                        <textarea
                            className="w-full min-h-[5rem] resize-y rounded-[3px] border px-ds-space-100 py-ds-space-100 font-[Inter] text-[0.875rem] leading-ds-space-250 outline-none"
                            style={{
                                borderColor: token("color.border.input", "#8590A2"),
                                background: token("color.background.input", "#FFFFFF"),
                                color: token("color.text", "#172B4D"),
                            }}
                            value={tempRemarks ?? ""}
                            onChange={(event) => {
                                setTempRemarks(event.currentTarget.value);
                            }}
                            placeholder="Type your call remarks"
                        />
                    </div>
                </div>
            )}
            {hideActions !== true ? (
                <div className="flex gap-2 justify-end pt-2">
                    <DispositionPanelFooterActions
                        saveLabel="Update"
                        saveDisabledReason={dispositionSaveDisabledReason}
                        isSavePending={isDispositionSavePending}
                        onCancel={onDispositionCancel}
                        onSave={onDispositionSave}
                    />
                </div>
            ) : null}
        </>
    );
}
